import type { TextChain } from '@/api/types/stowed-element'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import { logger } from '@/utils/logger'
import type { PortPoint } from './types/port-point'

/**
 * Square hit-target size (screen px) for an in/out port, centered on its edge-midpoint anchor —
 * half of it necessarily falls outside the frame's own rect. `text-linking-layer.tsx` renders in
 * screen space (a sibling of the canvas's `transform: scale()` wrapper, same as selection chrome),
 * so every port consumer — `PortSquare`, `LinkedOutPortSquare`, and `OutPortPlusGlyph` alike, all in
 * `text-linking/components/` — uses this value directly with no zoom conversion.
 *
 * Also shared with pointer hover-tracking (which frame is the Text Tool hovering, to show its
 * out-port "+"): that check runs in *document* space (`findLinkableFrameAtDocPoint`,
 * `center-canvas/hooks/use-text-link-drag.ts`), so it divides this by the current zoom before using
 * it as a margin — otherwise the pointer would "leave" the frame the instant it reaches the port.
 */
export const PORT_HIT_SIZE = 24

/**
 * Extra outward push (screen px) applied to *numbered* chain-sequence badges (in-ports and linked
 * out-ports). Zero (WMPCM-9199) — a numbered badge now sits flush on the frame's border, centered
 * exactly like the unlinked out-port "+" glyph, instead of floating clear of it. This used to be 6,
 * to keep the numeral's square from crowding the frame's first/last text line; that crowding is an
 * accepted, currently-unreported trade-off against the confirmed, screenshotted "port floats off
 * its frame" bug this constant caused. Revisit with a real value here only if crowding is reported
 * again, and prefer decoupling the numeral's position from the badge container's rather than
 * reintroducing this flat push.
 *
 * Passed straight through as the `offset` param below, which `text-linking-layer.tsx` calls
 * `portNumberOffset` — no scale conversion, since both the offset and the squares it positions are
 * screen px now.
 */
export const PORT_NUMBER_OFFSET_PX = 0

function rotatePoint(point: PortPoint, center: PortPoint, rotationDegrees: number): PortPoint {
  if (!rotationDegrees) return point
  const rad = (rotationDegrees * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const dx = point.x - center.x
  const dy = point.y - center.y

  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  }
}

/**
 * Top-center in-port, sitting on the frame's top edge midpoint (or an explicit outward offset),
 * rotation-aware.
 *
 * `pivot` defaults to `frame`'s own center, which is correct when `frame` IS the shape's geometric
 * rect. A shape-backed frame instead passes its *visual* rect here (see
 * `linkingChromeRectOfFrame`) so the port sits on the visible border, not the padding an inset
 * (triangle, star, custom path) leaves around it — but a rotation must still pivot on the shape's
 * geometric center, which for an asymmetric inset is NOT the visual rect's own center. Callers
 * doing that must pass that geometric pivot explicitly rather than rely on this default.
 */
export function inPortPoint(
  frame: {
    x: number
    y: number
    width: number
    height: number
    rotation?: number
  },
  offset = 0,
  pivot?: PortPoint
): PortPoint {
  const center = pivot ?? { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 }
  const raw = { x: frame.x + frame.width / 2, y: frame.y - offset }

  return rotatePoint(raw, center, frame.rotation ?? 0)
}

/**
 * Bottom-center out-port, sitting on the frame's bottom edge midpoint (or an explicit offset for
 * differently-anchored elements), rotation-aware. See `inPortPoint` for the `pivot` contract.
 */
export function outPortPoint(
  frame: {
    x: number
    y: number
    width: number
    height: number
    rotation?: number
  },
  offset = 0,
  pivot?: PortPoint
): PortPoint {
  const center = pivot ?? { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 }
  const raw = { x: frame.x + frame.width / 2, y: frame.y + frame.height + offset }

  return rotatePoint(raw, center, frame.rotation ?? 0)
}

/** Cubic bezier `d` attribute connecting two ports with vertical control handles. */
export function connectorPathD(from: PortPoint, to: PortPoint): string {
  const dy = Math.max(Math.abs(to.y - from.y) / 2, 24)
  const c1 = { x: from.x, y: from.y + dy }
  const c2 = { x: to.x, y: to.y - dy }

  return `M ${from.x} ${from.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${to.x} ${to.y}`
}

/**
 * 1-based chain positions for every member of every chain, including the first frame (numbered 1
 * at its out-port, matching every follower's in-port numbering).
 */
export function chainSequenceByFrameId(chains: LinkableTextFrame[][]): Map<string, number> {
  const result = new Map<string, number>()

  for (const chain of chains) {
    for (let i = 0; i < chain.length; i++) {
      result.set(chain[i]!.id, i + 1)
    }
  }

  return result
}

/** Resolves wire-shaped chains (uid strings) to live frame data via `String(frame.uid)`. */
export function resolveChainsToFrames(
  chains: TextChain[],
  frames: LinkableTextFrame[]
): LinkableTextFrame[][] {
  const byUid = new Map<string, LinkableTextFrame>()

  for (const frame of frames) {
    if (frame.uid == null) continue
    const key = String(frame.uid)

    if (byUid.has(key)) {
      logger.warn(
        `Duplicate text frame uid ${key} — ignoring frame ${frame.id}, keeping ${byUid.get(key)?.id}`
      )
      continue
    }

    byUid.set(key, frame)
  }

  const resolved: LinkableTextFrame[][] = []

  for (const chain of chains) {
    const members: LinkableTextFrame[] = []

    for (const tb of chain.textBox) {
      const frame = byUid.get(tb.uid)

      if (frame) members.push(frame)
    }

    if (members.length >= 2) resolved.push(members)
  }

  return resolved
}
